import { useState } from "react";
import { BrandLogo } from "@/components/BrandLogo";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthSideImage } from "@/components/AuthSideImage";
import { useSite } from "@/context/SiteContext";

export default function Register() {
  const { register, formatApiErrorDetail } = useAuth();
  const { config, t } = useSite();
  const ob = config?.onboarding || {};
  const minLen = config?.privacy?.min_password_length || 8;
  const closed = config?.privacy?.allow_registration === false;
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < minLen) {
      setError(`Password must be at least ${minLen} characters.`);
      return;
    }
    setLoading(true);
    try {
      const u = await register(form.name, form.email, form.password);
      toast.success(`${t(ob.welcome_message || "Welcome to Jollanda!")} ${u.name?.split(" ")[0] || ""}`);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#fbf9f7]" data-testid="register-page">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-[#d00000] transition-colors mb-10" data-testid="back-home">
            <ArrowLeft className="w-4 h-4" /> {t("Back to Jollanda")}
          </Link>
          <div className="mb-8">
            <BrandLogo className="h-14" testId="register-logo-link" />
          </div>
          <h1 className="font-display text-4xl font-black text-neutral-900" data-testid="register-title">{t(ob.register_title || "Join the table.")}</h1>
          <p className="text-neutral-500 mt-2">{t(ob.register_subtitle || "")}</p>
          {closed && <p className="mt-6 p-4 border border-neutral-200 bg-white text-sm text-neutral-700" data-testid="registration-closed">{t("New registrations are currently closed.")}</p>}

          <form onSubmit={submit} className={`mt-8 space-y-5 ${closed ? "hidden" : ""}`}>
            <div>
              <Label className="text-neutral-500 text-xs uppercase tracking-widest">Full name</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ada Obi" data-testid="register-name"
                className="mt-2 h-12 bg-white border-neutral-300 text-neutral-900 rounded-none focus-visible:ring-[#d00000]" />
            </div>
            <div>
              <Label className="text-neutral-500 text-xs uppercase tracking-widest">Email</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" data-testid="register-email"
                className="mt-2 h-12 bg-white border-neutral-300 text-neutral-900 rounded-none focus-visible:ring-[#d00000]" />
            </div>
            <div>
              <Label className="text-neutral-500 text-xs uppercase tracking-widest">Password</Label>
              <Input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={`At least ${minLen} characters`} data-testid="register-password"
                className="mt-2 h-12 bg-white border-neutral-300 text-neutral-900 rounded-none focus-visible:ring-[#d00000]" />
            </div>
            {error && <p className="text-sm text-[#d00000]" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full h-12 bg-[#d00000] hover:bg-[#a30000] text-white rounded-none">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t(ob.register_button || "Create account")}
            </Button>
          </form>

          <p className="mt-8 text-sm text-neutral-500">
            Already have an account? <Link to="/login" className="text-neutral-900 underline hover:text-[#d00000]" data-testid="go-login">Sign in</Link>
          </p>
        </motion.div>
      </div>

      <AuthSideImage image={ob.register_image} text={ob.register_side_text} t={t} className="order-1 lg:order-2" />
    </div>
  );
}
